import { useMutation, useQuery } from "@tanstack/react-query";
import {
  EducationalResourceCreateRequestPayload,
  EducationalResourceUpdateRequestPayload,
  SearchEducationalResourcePayload,
} from "@/domain/educational-resource.interface";
import { EducationalResourcesService } from "@/services/educational-resources.service";

export const useEducationalResources = (
  {
    sortField,
    sortOrder,
    pageNumber,
    pageSize,
    search,
    categoryId,
    type,
    status,
  }: SearchEducationalResourcePayload,
  enabled = true,
) => {
  const {
    data: educationalResourcesResponse,
    isLoading: isLoadingEducationalResources,
    refetch: refetchEducationalResources,
  } = useQuery({
    queryKey: [
      "coyn-educational-resources",
      sortField,
      sortOrder,
      pageNumber,
      pageSize,
      search,
      categoryId,
      type,
      status,
    ],
    queryFn: () =>
      EducationalResourcesService.search({
        sortField,
        sortOrder,
        pageNumber,
        pageSize,
        search,
        categoryId,
        type,
        status,
      }),
    enabled,
  });

  return {
    educationalResources: educationalResourcesResponse?.educationalResources,
    totalCount: educationalResourcesResponse?.totalCount,
    isLoadingEducationalResources,
    refetchEducationalResources,
  };
};

export const useEducationalResourceCreateOne = () => {
  return useMutation({
    mutationFn: (data: EducationalResourceCreateRequestPayload) =>
      EducationalResourcesService.create(data),
  });
};

export const useEducationalResourceUpdateOne = () => {
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: EducationalResourceUpdateRequestPayload;
    }) => {
      await EducationalResourcesService.update({ id, data });
    },
  });
};

export const useEducationalResourceDeleteOne = () => {
  return useMutation({
    mutationFn: (id: string) => EducationalResourcesService.softDelete(id),
  });
};

export const useEducationalResourceById = (id: string, enabled = true) => {
  const {
    data: educationalResource,
    isLoading: isLoadingEducationalResource,
    refetch: refetchEducationalResource,
  } = useQuery({
    queryKey: ["coyn-educational-resource", id],
    queryFn: () => EducationalResourcesService.getById(id),
    enabled: enabled && !!id,
  });

  return {
    educationalResource,
    isLoadingEducationalResource,
    refetchEducationalResource,
  };
};
